import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Brain, Cpu, Rocket, Check, ArrowRight, Zap, BarChart3, Users, Target } from 'lucide-react';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { ParticleBackground } from '../components/ParticleBackground';
import { DynamicHeroBackground } from '../components/DynamicHeroBackground';
import { LeadMagnet } from '../components/LeadMagnet';
import { featuredProjects } from '../data/projects';

const ASTRONAUT_IMAGE = '/images/brand/hero-slide1.jpg';

// Behance'dan alınan gerçek portfolio verileri artık data/projects.js'den geliyor
// featuredProjects import edildi

const services = [
  {
    id: 'dijital-deneyim',
    title: 'Dijital Deneyim & Algı Mimarisi',
    description: 'Web tasarımı değil, zihinlerde iz bırakan deneyim mimarisi. Nöro-UX prensipleriyle kullanıcılarınızın bilinçaltına hitap eden dijital deneyimler.',
    icon: Brain,
    href: '/buyume-sistemleri/dijital-deneyim-algi-mimarisi',
  },
  {
    id: 'otorite-talep',
    title: 'Otorite & Talep Motoru',
    description: 'SEO değil, sektörünüzde otorite olmanızı sağlayan içerik sistemi. Organik trafik ve güvenilirlik aynı anda.',
    icon: Target,
    href: '/buyume-sistemleri/otorite-talep-motoru',
  },
  {
    id: 'performans-ikna',
    title: 'Performans & İkna Hızlandırıcı',
    description: 'Reklam değil, dönüşüm psikolojisi ile tasarlanmış büyüme kampanyaları. Her TL\'den maksimum geri dönüş.',
    icon: Rocket,
    href: '/buyume-sistemleri/performans-ikna-hizlandirici',
  },
];

const aifabricFeatures = [
  'Gerçek zamanlı kullanıcı davranış analizi',
  'Otomatik A/B test optimizasyonu',
  'Tahmine dayalı içerik önerileri',
  'Dönüşüm hunisi otomasyon motoru',
];

const stats = [
  { value: 150, suffix: '+', label: 'Proje' },
  { value: 240, prefix: '%', label: 'Ort. Büyüme' },
  { value: 20, label: 'Yıl Deneyim' },
  { value: 50, suffix: '+', label: 'Mutlu Müşteri' },
];



const stepItems = [
  { icon: Brain, title: 'ZİHNE GİRİYORUZ', description: 'İnsanların neye tıkladığını değil,\nneden karar verdiğini çözümlüyoruz.', num: '01', accent: 'from-violet-500 to-purple-600', glowColor: 'rgba(139,92,246,0.2)' },
  { icon: Cpu, title: 'SİSTEME BAĞLIYORUZ', description: 'Büyümeyi kişilere değil,\nçalışan sistemlere bağlıyoruz.', num: '02', accent: 'from-cyan-500 to-blue-600', glowColor: 'rgba(6,182,212,0.2)' },
  { icon: Rocket, title: 'SONUÇ ÜRETİYORUZ', description: 'Ölçülemeyen hiçbir şeyi\nbaşarı olarak kabul etmiyoruz.', num: '03', accent: 'from-emerald-500 to-teal-600', glowColor: 'rgba(16,185,129,0.2)' },
];

function StepCards() {
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: '-100px' });
  const [activeStep, setActiveStep] = useState(-1);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    if (isInView && !hasEntered) setHasEntered(true);
  }, [isInView, hasEntered]);

  // Sonsuz döngü: 0 → 1 → 2 → 0 → 1 → 2 ...
  // Hover varsa döngü durur
  useEffect(() => {
    if (!isInView || hoveredStep !== null) return;
    const cycle = () => {
      setActiveStep(prev => (prev + 1) % 3);
    };
    // İlk tetikleme hemen
    cycle();
    const interval = setInterval(cycle, 1800);
    return () => clearInterval(interval);
  }, [isInView, hoveredStep]);

  // Hover bırakınca döngü kaldığı yerden devam eder (useEffect tekrar çalışır)
  const handleMouseEnter = (index) => {
    setHoveredStep(index);
    setActiveStep(index);
  };
  const handleMouseLeave = () => {
    setHoveredStep(null);
  };

  // Aktif olan: hover varsa hover, yoksa döngüdeki
  const currentActive = hoveredStep !== null ? hoveredStep : activeStep;

  return (
    <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
      {stepItems.map((item, index) => {
        const isActive = currentActive === index;
        return (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 60, scale: 0.92 }}
            animate={hasEntered ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: index * 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative cursor-pointer"
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
          >
            {/* Gradient top border */}
            <div
              className={`absolute top-0 h-[2px] bg-gradient-to-r ${item.accent} transition-all duration-700`}
              style={{
                left: isActive ? 24 : 32,
                right: isActive ? 24 : 32,
                opacity: isActive ? 1 : 0.3,
              }}
            />

            <div
              className="relative p-8 lg:p-10 h-full rounded-2xl border bg-zinc-900/30 backdrop-blur-sm overflow-hidden transition-all duration-700"
              style={{
                borderColor: isActive ? 'rgba(161,161,170,0.25)' : 'rgba(39,39,42,0.5)',
                transform: isActive ? 'translateY(-4px)' : 'translateY(0)',
              }}
            >
              {/* Watermark number */}
              <span
                className="absolute top-6 right-8 text-[5rem] font-black leading-none select-none transition-colors duration-700"
                style={{ color: isActive ? 'rgba(63,63,70,0.25)' : 'rgba(39,39,42,0.15)' }}
              >
                {item.num}
              </span>

              {/* Auto glow */}
              <div
                className="absolute -top-20 -right-20 w-40 h-40 rounded-full transition-opacity duration-700"
                style={{
                  background: `radial-gradient(circle, ${item.glowColor} 0%, transparent 70%)`,
                  filter: 'blur(40px)',
                  opacity: isActive ? 1 : 0,
                }}
              />

              <div className="relative z-10">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.accent} p-[1px] mb-8`}>
                  <div className="w-full h-full rounded-2xl bg-zinc-900 flex items-center justify-center">
                    <item.icon
                      className="w-6 h-6 transition-colors duration-500"
                      style={{ color: isActive ? '#fff' : 'rgb(212,212,216)' }}
                    />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white mb-4 tracking-tight">{item.title}</h3>
                <p className="text-zinc-400 leading-relaxed whitespace-pre-line text-[0.95rem]">{item.description}</p>
              </div>
            </div>

            {/* Connector arrow */}
            {index < 2 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={hasEntered ? { opacity: 1 } : {}}
                transition={{ duration: 0.4, delay: index * 0.3 + 0.6 }}
                className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 items-center justify-center"
              >
                <div className="w-6 h-[1px] bg-gradient-to-r from-zinc-700 to-zinc-600" />
                <ArrowRight className="w-3 h-3 text-zinc-600 -ml-1" />
              </motion.div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

const timelineItems = [
  { step: '01', title: 'OKURUZ', body: 'Markayı, sektörü ve hedef kitlenin karar anlarını analiz ederiz. Sorun ne göründüğü değil, neden tekrar ettiğiyle ilgilidir.', icon: Brain, accent: 'violet', glowColor: 'rgba(139,92,246,0.2)' },
  { step: '02', title: 'KURARIZ', body: 'Web, içerik, reklam ve yapay zekâyı tek bir büyüme sistemine bağlarız. Parça parça işler değil, çalışan bir düzen oluştururuz.', icon: Cpu, accent: 'cyan', glowColor: 'rgba(6,182,212,0.2)' },
  { step: '03', title: 'TEST EDERİZ', body: 'Varsayım değil, veriyle ilerleriz. İşe yaramayan her şey sistemden çıkarılır.', icon: Rocket, accent: 'emerald', glowColor: 'rgba(16,185,129,0.2)' },
];

function MethodologyTimeline() {
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: '-100px' });
  const [activeStep, setActiveStep] = useState(-1);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    if (isInView && !hasEntered) setHasEntered(true);
  }, [isInView, hasEntered]);

  useEffect(() => {
    if (!isInView || hoveredStep !== null) return;
    const cycle = () => setActiveStep(prev => (prev + 1) % 3);
    cycle();
    const interval = setInterval(cycle, 1800);
    return () => clearInterval(interval);
  }, [isInView, hoveredStep]);

  const handleMouseEnter = (index) => { setHoveredStep(index); setActiveStep(index); };
  const handleMouseLeave = () => { setHoveredStep(null); };
  const currentActive = hoveredStep !== null ? hoveredStep : activeStep;

  const accentGradient = (a) => a === 'violet' ? 'from-violet-500 to-purple-600' : a === 'cyan' ? 'from-cyan-500 to-blue-600' : 'from-emerald-500 to-teal-600';
  const accentBorder = (a) => a === 'violet' ? 'border-violet-500 bg-violet-500/30' : a === 'cyan' ? 'border-cyan-500 bg-cyan-500/30' : 'border-emerald-500 bg-emerald-500/30';

  return (
    <div ref={ref} className="relative">
      <div className="absolute left-6 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-zinc-700/40 to-transparent hidden sm:block" />

      <div className="lg:-space-y-2">
        {timelineItems.map((item, index) => {
          const isActive = currentActive === index;
          return (
            <motion.div
              key={item.step}
              initial={{ opacity: 0, y: 40 }}
              animate={hasEntered ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className={`relative flex flex-col md:flex-row items-start gap-3 lg:gap-6 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''} lg:py-1`}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            >
              {/* Timeline dot */}
              <div className="absolute left-6 md:left-1/2 -translate-x-1/2 top-6 z-10 hidden sm:block">
                <div
                  className={`w-3 h-3 rounded-full border-2 transition-all duration-500 ${accentBorder(item.accent)}`}
                  style={{ transform: isActive ? 'scale(1.4)' : 'scale(1)', boxShadow: isActive ? `0 0 12px ${item.glowColor}` : 'none' }}
                />
              </div>

              {/* Card */}
              <div className={`flex-1 ${index % 2 === 1 ? 'md:text-right' : ''} ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                <div
                  className="relative p-6 lg:p-7 rounded-2xl border bg-zinc-900/40 backdrop-blur-sm overflow-hidden cursor-pointer transition-all duration-600"
                  style={{
                    borderColor: isActive ? 'rgba(161,161,170,0.25)' : 'rgba(39,39,42,0.5)',
                    transform: isActive ? 'translateY(-3px)' : 'translateY(0)',
                  }}
                >
                  {/* Glow */}
                  <div
                    className={`absolute -top-14 ${index % 2 === 1 ? '-left-14' : '-right-14'} w-28 h-28 rounded-full transition-opacity duration-700`}
                    style={{ background: `radial-gradient(circle, ${item.glowColor} 0%, transparent 70%)`, filter: 'blur(40px)', opacity: isActive ? 1 : 0 }}
                  />

                  {/* Step watermark */}
                  <span
                    className={`absolute top-3 ${index % 2 === 1 ? 'left-5' : 'right-5'} text-[4rem] font-black leading-none select-none transition-colors duration-700`}
                    style={{ color: isActive ? 'rgba(63,63,70,0.25)' : 'rgba(39,39,42,0.12)' }}
                  >{item.step}</span>

                  <div className={`relative z-10 flex items-start gap-4 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                    <div className={`flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${accentGradient(item.accent)} p-[1px]`}>
                      <div className="w-full h-full rounded-xl bg-zinc-900 flex items-center justify-center">
                        <item.icon className="w-5 h-5 transition-colors duration-500" style={{ color: isActive ? '#fff' : 'rgb(212,212,216)' }} />
                      </div>
                    </div>
                    <div className={index % 2 === 1 ? 'md:text-right' : ''}>
                      <h3 className="text-base font-bold text-white mb-2 tracking-tight">{item.title}</h3>
                      <p className="text-zinc-400 leading-relaxed text-sm">{item.body}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 hidden md:block" />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}



const testimonials = [
  { quote: 'Açık konuşmak gerekirse biz ajans aramıyorduk… çözüm arıyorduk. Taner Bey\'e süreci bıraktıktan sonra fark ettik ki biz bugüne kadar sadece \'denemişiz\'. Markayı bizden daha çok sahiplenen biriyle çalışmak bambaşka bir deneyimmiş.', name: 'K.A.', role: 'CEO / Kurucu' },
  { quote: 'Rakamlarla konuşan biriyim. İlk 2 ayda sistemin çalıştığını net şekilde gördük. Ama asıl fark, Taner Bey\'in hata yapmamıza izin vermemesi. Deneyim dediğimiz şey tam olarak bu.', name: 'S.Y.', role: 'CFO / Üst Yönetim' },
  { quote: 'Bir noktadan sonra müdahale etmeyi bıraktım. Çünkü şunu fark ettim; biz markayı yönetiyorduk, Taner Bey ise büyütüyordu. O günden sonra sadece izlemeye başladım.', name: 'H.D.', role: 'Marka Sahibi' },
  { quote: 'Web sitemiz vardı ama çalışmıyordu. Şimdi sistem var ve satış geliyor. Aradaki farkı anlatmak zor ama yaşayan anlıyor.', name: 'M.E.', role: 'E-Ticaret Girişimcisi' },
  { quote: 'En büyük fark şu oldu: Bizi \'iyi fikir\' sandığımız birçok şeyden vazgeçirdi. Ve o sayede gerçekten doğru yola girdik. Bugün geldiğimiz nokta o kararların sonucu.', name: 'B.T.', role: 'Yönetici' },
  { quote: 'Fikrimizi anlattık, Taner Bey 10 dakika dinledi. Sonra öyle bir çerçeve çizdi ki… bizim 6 ayda göremediğimizi 1 saatte netleştirdi.', name: 'C.K.', role: 'Startup Kurucusu' },
  { quote: 'Satış konuşmayı bıraktık, sistem kurmaya başladık. O günden sonra zaten satış kendiliğinden geldi.', name: 'E.A.', role: 'Satış Odaklı İşletme' },
  { quote: 'Taner Bey ile çalışmak bir hizmet almak gibi değil. Daha çok… işin içinde bir \'akıl\' olması gibi. Karar verirken yalnız olmadığınızı hissediyorsunuz.', name: 'N.Ö.', role: 'Premium Segment Müşteri' },
  { quote: 'Marka ismimizden hikayesine kadar birçok şeyi birlikte şekillendirdik. Bugün dönüp baktığımda… iyi ki kontrolü bırakmışım diyorum.', name: 'A.L.', role: 'Kreatif İş Sahibi' },
  { quote: 'Eskiden trafik arıyorduk, şimdi müşteri geliyor. Aradaki farkı yaratan şey reklam değilmiş… sistemmiş.', name: 'R.G.', role: 'İşletme Sahibi' },
  { quote: 'Taner Bey sadece markayı değil, bakış açınızı değiştiriyor. O yüzden sonuçlar sadece işte değil, düşünme şeklinizde de görülüyor.', name: 'F.B.', role: 'Kurucu Ortak' },
  { quote: 'Markayı bırakın demişti… bıraktık. İlk defa doğru büyümenin ne olduğunu gördük.', name: 'D.S.', role: 'Marka Sahibi' },
];

const StarRating = () => (
  <div className="flex gap-[3px]">
    {[...Array(5)].map((_, i) => (
      <svg key={i} width="12" height="12" viewBox="0 0 20 20">
        <defs><linearGradient id={`sg${i}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#fbbf24" /><stop offset="100%" stopColor="#f59e0b" /></linearGradient></defs>
        <path d="M10 1l2.39 4.84 5.34.78-3.87 3.77.91 5.33L10 13.27l-4.77 2.51.91-5.33L2.27 6.68l5.34-.78L10 1z" fill={`url(#sg${i})`} />
      </svg>
    ))}
  </div>
);

const avatarGradients = [
  'from-violet-500/70 to-purple-700/70',
  'from-cyan-500/70 to-blue-700/70',
  'from-emerald-500/70 to-teal-700/70',
  'from-amber-500/70 to-orange-700/70',
  'from-rose-500/70 to-pink-700/70',
  'from-indigo-500/70 to-violet-700/70',
  'from-teal-500/70 to-cyan-700/70',
  'from-fuchsia-500/70 to-purple-700/70',
  'from-sky-500/70 to-blue-700/70',
  'from-lime-500/70 to-green-700/70',
  'from-orange-500/70 to-red-700/70',
  'from-blue-500/70 to-indigo-700/70',
];

function TestimonialCard({ t, index }) {
  const grad = avatarGradients[index % avatarGradients.length];
  return (
    <div className="flex-shrink-0 w-[400px] mx-3">
      <div className="relative p-8 rounded-[20px] border border-zinc-800/30 bg-gradient-to-br from-zinc-900/80 via-zinc-900/50 to-zinc-950/80 backdrop-blur-xl h-full group hover:border-zinc-700/40 transition-all duration-700 overflow-hidden">
        {/* Inner glow top-left */}
        <div className="absolute top-0 left-0 w-48 h-48 rounded-full bg-white/[0.015] blur-[50px] pointer-events-none" />

        {/* Large quote mark */}
        <svg className="absolute top-5 right-6 w-10 h-10 text-zinc-800/30 group-hover:text-zinc-700/30 transition-colors duration-700" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
        </svg>

        {/* Quote text */}
        <p className="text-[0.95rem] text-zinc-300/90 leading-[1.85] mb-8 relative z-10 pr-8">
          {t.quote}
        </p>

        {/* Divider */}
        <div className="w-12 h-[1px] bg-gradient-to-r from-zinc-700/60 to-transparent mb-5" />

        {/* Footer */}
        <div className="flex items-center justify-between relative z-10">
          <div className="flex items-center gap-3.5">
            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${grad} flex items-center justify-center text-[11px] font-bold text-white/90 shadow-lg`}>
              {t.name.charAt(0)}
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <p className="text-[13px] font-semibold text-white tracking-tight">{t.name}</p>
                <svg width="14" height="14" viewBox="0 0 48 48" className="opacity-60">
                  <polygon fill="#8b5cf6" points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884" />
                  <polygon fill="#fff" points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926" />
                </svg>
              </div>
              <p className="text-[11px] text-zinc-500 mt-0.5">{t.role}</p>
            </div>
          </div>
          <StarRating />
        </div>
      </div>
    </div>
  );
}

function TestimonialGrid() {
  const row1 = testimonials.slice(0, 6);
  const row2 = testimonials.slice(6);

  return (
    <div className="space-y-5">
      {/* Row 1 — sola kayma */}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 lg:w-48 bg-gradient-to-r from-[#0B0B0B] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 lg:w-48 bg-gradient-to-l from-[#0B0B0B] to-transparent z-10 pointer-events-none" />
        <div className="testimonial-track">
          <div className="testimonial-scroll">
            {[...row1, ...row1].map((t, i) => <TestimonialCard key={`r1a-${i}`} t={t} index={i} />)}
          </div>
          <div className="testimonial-scroll" aria-hidden="true">
            {[...row1, ...row1].map((t, i) => <TestimonialCard key={`r1b-${i}`} t={t} index={i} />)}
          </div>
        </div>
      </div>

      {/* Row 2 — sağa kayma */}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 lg:w-48 bg-gradient-to-r from-[#0B0B0B] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 lg:w-48 bg-gradient-to-l from-[#0B0B0B] to-transparent z-10 pointer-events-none" />
        <div className="testimonial-track">
          <div className="testimonial-scroll-reverse">
            {[...row2, ...row2].map((t, i) => <TestimonialCard key={`r2a-${i}`} t={t} index={i + 6} />)}
          </div>
          <div className="testimonial-scroll-reverse" aria-hidden="true">
            {[...row2, ...row2].map((t, i) => <TestimonialCard key={`r2b-${i}`} t={t} index={i + 6} />)}
          </div>
        </div>
      </div>
    </div>
  );
}



const manifestoItems = [
  { title: 'Sadece Görünmek İsteyenlerle', body: 'Sosyal medyada var olmak isteyen ama işine etkisini ölçmek istemeyen markalarla çalışmayız.' },
  { title: 'Sürekli Değişmek İsteyenlerle', body: 'Her hafta fikrini değiştiren, sistem yerine anlık heyecan arayan yapılarla ilerlemeyiz.' },
  { title: 'Sonuçtan Kaçanlarla', body: 'Veriyle yüzleşmekten çekinen, başarısız ihtimali kabul etmeyenlerle aynı masaya oturmayız.' },
];

function ManifestoCards() {
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: '-100px' });
  const [activeCard, setActiveCard] = useState(-1);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    if (isInView && !hasEntered) setHasEntered(true);
  }, [isInView, hasEntered]);

  useEffect(() => {
    if (!isInView || hoveredCard !== null) return;
    const cycle = () => setActiveCard(prev => (prev + 1) % 3);
    cycle();
    const interval = setInterval(cycle, 2400);
    return () => clearInterval(interval);
  }, [isInView, hoveredCard]);

  const handleEnter = (i) => { setHoveredCard(i); setActiveCard(i); };
  const handleLeave = () => { setHoveredCard(null); };
  const current = hoveredCard !== null ? hoveredCard : activeCard;

  return (
    <div ref={ref} className="max-w-2xl mx-auto divide-y divide-zinc-800/40">
      {manifestoItems.map((item, index) => {
        const isActive = current === index;
        return (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, x: -20 }}
            animate={hasEntered ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="py-8 cursor-pointer group"
            onMouseEnter={() => handleEnter(index)}
            onMouseLeave={handleLeave}
          >
            <div className="flex items-start gap-6">
              {/* Number */}
              <span
                className="text-4xl font-black leading-none transition-all duration-600 select-none flex-shrink-0 w-12"
                style={{
                  color: isActive ? 'rgba(161,161,170,0.4)' : 'rgba(63,63,70,0.25)',
                }}
              >
                {String(index + 1).padStart(2, '0')}
              </span>

              <div className="flex-1">
                {/* Title with strikethrough effect */}
                <div className="relative inline-block mb-3">
                  <h3
                    className="text-lg font-bold tracking-tight transition-colors duration-500"
                    style={{ color: isActive ? 'rgba(161,161,170,0.5)' : '#fff' }}
                  >
                    {item.title}
                  </h3>
                  {/* Strikethrough line */}
                  <div
                    className="absolute top-1/2 left-0 h-[2px] bg-zinc-500 transition-all duration-700 ease-out"
                    style={{
                      width: isActive ? '100%' : '0%',
                      opacity: isActive ? 0.6 : 0,
                    }}
                  />
                </div>

                <p
                  className="text-sm leading-relaxed transition-all duration-500"
                  style={{
                    color: isActive ? 'rgba(161,161,170,0.7)' : 'rgba(161,161,170,0.5)',
                    transform: isActive ? 'translateX(4px)' : 'translateX(0)',
                  }}
                >
                  {item.body}
                </p>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}


const allClients = [
  'İnnovacare', 'K-Lotus Beauty', 'Bipet', 'Trendy Art', 'Wetnose', 'Anticorex',
  'Sasasapanca', 'Kids A more', 'Eczaonline', 'Enmama', 'PetQueen', 'DeltaCanlı',
  'İbrahim BIYIKLI', 'DevFest', 'Alya Butik Otel', 'Ümit Usta', 'ZooVital',
  'Skyfall Lounge', 'Gölevi', 'Reborn Beauty', 'Özpay', 'Maryap', 'Livera',
];
const allClients2 = [
  'Ledmarketim', 'KıtırMama', 'Katıkçı Baba', 'Kasaphan', 'Vitamor',
  'Neslihan TÜRKOĞLU', 'Dr. Gürdal Ören', 'Derindere Hastanesi', 'Atakent Hastanesi',
  'OPARK', 'Nokta Medya', 'Ekin Backerei', 'Chefİstanbul', 'Baristaplus',
  'Kollektif House', 'Jolly Joker', 'İZAYDAŞ', 'Elbet', 'SketchPlus',
  'Golden Palace', 'Garnet Home', 'Prof. Dr. Tonguç İŞKEN',
];

function ClientMarquee() {
  return (
    <div className="space-y-6">
      {/* Sıra 1 — sola kayma */}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#0A0A0A] to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#0A0A0A] to-transparent z-10" />
        <div className="marquee-track">
          <div className="marquee-scroll">
            {[...allClients, ...allClients].map((name, i) => (
              <span key={`a-${i}`} className="marquee-item">{name}</span>
            ))}
          </div>
          <div className="marquee-scroll" aria-hidden="true">
            {[...allClients, ...allClients].map((name, i) => (
              <span key={`b-${i}`} className="marquee-item">{name}</span>
            ))}
          </div>
        </div>
      </div>
      {/* Sıra 2 — sağa kayma */}
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#0A0A0A] to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#0A0A0A] to-transparent z-10" />
        <div className="marquee-track">
          <div className="marquee-scroll-reverse">
            {[...allClients2, ...allClients2].map((name, i) => (
              <span key={`c-${i}`} className="marquee-item">{name}</span>
            ))}
          </div>
          <div className="marquee-scroll-reverse" aria-hidden="true">
            {[...allClients2, ...allClients2].map((name, i) => (
              <span key={`d-${i}`} className="marquee-item">{name}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-mesh relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Dinamik Astronot Arka Plan - Parallax + Floating + Kozmik Efektler */}
        <DynamicHeroBackground imageUrl={ASTRONAUT_IMAGE} />
        
        {/* Particle Efektleri */}
        <ParticleBackground particleCount={40} />
        
        <div className="container-wide relative z-10 text-center pt-32 lg:pt-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="inline-block px-4 py-2 mb-6 text-xs font-medium text-zinc-400 bg-zinc-800/50 rounded-full border border-zinc-700/40">
              aifabric ile güçlendirildi
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="heading-hero text-white mb-6"
            data-testid="hero-title"
          >
            Dijital Büyümeyi
            <br />
            <span className="gradient-text-refined text-[1.1em] tracking-[-0.025em]">Biz Tasarlarız.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-base md:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Markalar için büyümeyi bir tahmin değil,
            <br className="hidden sm:block" />
            çalışan bir sistem haline getiriyoruz.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link 
              to="/buyume-yolculugu" 
              className="btn-gradient-refined flex items-center gap-2"
              data-testid="hero-cta-primary"
            >
              Büyüme Yolculuğunu Başlat
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link 
              to="/portfolyo" 
              className="btn-glow-refined"
              data-testid="hero-cta-secondary"
            >
              Gerçek Sonuçları Gör
            </Link>
          </motion.div>
        </div>

        {/* Scroll Down Arrow */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 cursor-pointer"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          data-testid="scroll-indicator"
        >
          <motion.span
            className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-medium"
            animate={{ opacity: [0.4, 0.8, 0.4] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            Keşfet
          </motion.span>
          <motion.div
            className="w-[1px] h-8 bg-gradient-to-b from-zinc-500/60 to-transparent"
            animate={{ scaleY: [0.5, 1, 0.5], opacity: [0.3, 0.7, 0.3] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          </motion.div>
        </motion.div>
      </section>

      {/* Büyüme Ortaklarımız — Sonsuz Kayan Marquee */}
      <section className="py-16 bg-[#0A0A0A] border-b border-zinc-800/30 overflow-hidden" data-testid="trust-bar">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs font-mono text-zinc-600 tracking-widest uppercase mb-10 text-center">Büyüme ortaklarımız</p>
          <ClientMarquee />
        </motion.div>
      </section>

      {/* Medyatik Farkı Section - Updated */}
      <section className="section-padding bg-[#0A0A0A]" data-testid="difference-section">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-zinc-500 tracking-widest uppercase mb-4 block">
              Neden Medyatik?
            </span>
            <h2 className="heading-section text-white">
              Rakipleriniz Site Yaptırır.
              <br />
              <span className="gradient-text-refined text-[1.05em] font-extrabold">Siz Zihin Kurarsınız.</span>
            </h2>
          </motion.div>

          <StepCards />
        </div>
      </section>

      {/* Nasıl Çalışıyoruz - Methodology Section */}
      <section className="py-24 lg:py-32 bg-[#0D0D0D] relative overflow-hidden" data-testid="methodology-section-home">
        {/* Background Image - Right Side */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/assets/taner-sistem.png" 
            alt="" 
            className="absolute right-[-10%] top-1/2 -translate-y-1/2 h-[90%] w-auto object-contain opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/90 to-[#0D0D0D]/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D] via-transparent to-[#0D0D0D]" />
        </div>
        
        <div className="max-w-6xl mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <span className="text-xs font-mono text-zinc-600 tracking-widest uppercase block mb-5">Metodoloji</span>
            <h2 className="heading-section text-white mb-5">
              Ajans Gibi Çalışmayız.
              <br />
              <span className="gradient-text-refined">Sistem Kurarız.</span>
            </h2>
            <p className="text-zinc-400 text-lg max-w-lg mx-auto">
              Her adım ölçülür. Her kararın bir karşılığı vardır.
            </p>
          </motion.div>

          {/* Timeline Steps */}
          <MethodologyTimeline />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-16"
          >
            <Link 
              to="/metodoloji" 
              className="btn-gradient-refined inline-flex items-center gap-2"
              data-testid="methodology-home-cta"
            >
              Sistemi Gör
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="text-xs text-zinc-500 mt-3">3 dakikalık örnek analiz</p>
          </motion.div>
        </div>
      </section>

      {/* Kimlerle Çalışmıyoruz - Manifesto Section */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A] relative overflow-hidden" data-testid="filter-section">
        {/* Subtle background texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />

        <div className="max-w-5xl mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-zinc-600 tracking-widest uppercase block mb-5">Manifesto</span>
            <h2 className="heading-section text-white mb-4">
              Herkesle Çalışmayız.
            </h2>
            <p className="text-zinc-400 text-lg">
              Çünkü büyüme, herkes için aynı anlama gelmez.
            </p>
          </motion.div>

          <ManifestoCards />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-center mt-20"
          >
            <div className="inline-block px-10 py-6 rounded-2xl border border-zinc-800/50 bg-zinc-900/30 backdrop-blur-sm">
              <p className="text-zinc-400 text-lg leading-relaxed">
                Çünkü biz hizmet satmıyoruz.
              </p>
              <p className="text-xl font-bold mt-1">
                <span className="bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">Sorumluluk alıyoruz.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gerçek Sonuçlar - Proof Section (NEW) */}
      <section className="section-padding bg-[#0D0D0D]" data-testid="proof-section">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="heading-section text-white mb-4">
              Sonuçlar Slaytta Değil,
              <br />
              <span className="gradient-text-refined">Sistemin İçinde Görünür.</span>
            </h2>
            <p className="text-zinc-400 text-lg whitespace-pre-line">
              Biz referans anlatmayız.{'\n'}Ne değiştiğini gösteririz.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                title: 'Davranış Değişimi',
                body: 'Ziyaretçiler sayfada kalmıyordu.\nŞimdi karar vermeden çıkmıyorlar.',
                note: 'Zihin haritalaması + sayfa akışı yeniden kurgulandı.',
              },
              {
                title: 'Sistem Etkisi',
                body: 'Reklam bütçesi artmadı.\nAma her tıklamanın değeri yükseldi.',
                note: 'aifabric ile karar noktaları ayrıştırıldı.',
              },
              {
                title: 'Yönetici Dili',
                body: 'Artık "neden düşüyor?" değil,\n"hangi hamleyi yapıyoruz?" konuşuluyor.',
                note: 'Veri → karar → aksiyon döngüsü kuruldu.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <div className="card-refined p-8 h-full">
                  <h3 className="text-lg font-bold text-white mb-4">{item.title}</h3>
                  <p className="text-zinc-300 leading-relaxed whitespace-pre-line mb-4">{item.body}</p>
                  <p className="text-sm text-zinc-500 italic">{item.note}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <p className="text-zinc-400 text-lg whitespace-pre-line">
              Bu bir kampanya değil.{'\n'}
              <span className="text-white font-medium">Bu bir çalışma biçimi.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Social Proof - Testimonials */}
      <section className="py-28 lg:py-36 bg-[#0B0B0B] relative overflow-hidden" data-testid="social-proof">
        {/* Ambient lights */}
        <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-violet-600/[0.03] blur-[120px] pointer-events-none" />
        <div className="absolute bottom-20 right-1/4 w-80 h-80 rounded-full bg-cyan-600/[0.03] blur-[100px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <span className="text-xs font-mono text-zinc-600 tracking-widest uppercase block mb-5">Müşteri Deneyimleri</span>
            <h2 className="heading-section text-white mb-5">
              Onlar Anlatsın.
            </h2>
            <p className="text-zinc-500 text-base max-w-md mx-auto leading-relaxed">Birlikte büyüdüğümüz markalardan, kendi sözleriyle.</p>
          </motion.div>

          <TestimonialGrid />
        </div>
      </section>

      {/* Büyüme Sistemleri Section */}
      <section className="section-padding bg-[#121212]" data-testid="services-section">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-emerald-400 tracking-widest uppercase mb-4 block">
              Büyüme Sistemleri
            </span>
            <h2 className="heading-section text-white">
              Ölçeklenebilir Dijital Büyüme
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <Link to={service.href} data-testid={`service-card-${service.id}`}>
                  <div className="service-card group h-full">
                    <div className="relative z-10">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                        <service.icon className="w-8 h-8 text-indigo-400" />
                      </div>
                      <h3 className="heading-card text-white mb-4">{service.title}</h3>
                      <p className="text-zinc-400 mb-6 leading-relaxed">{service.description}</p>
                      <span className="inline-flex items-center gap-2 text-violet-400 font-medium group-hover:gap-3 transition-all duration-300">
                        Detayları Keşfet
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* aifabric Platform Showcase */}
      <section className="section-padding bg-[#0A0A0A] relative overflow-hidden" data-testid="aifabric-section">
        {/* AI Brain Background Image - Right Side */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/assets/ai-brain.png" 
            alt="" 
            className="absolute right-[-5%] top-1/2 -translate-y-1/2 h-[85%] w-auto object-contain opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#0A0A0A]" />
        </div>
        
        <div className="container-wide relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Code Visualization */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <div className="code-visual relative overflow-hidden">
                <div className="absolute top-4 left-4 flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="pt-10 space-y-2">
                  <p className="text-zinc-500">// aifabric büyüme motoru</p>
                  <p><span className="text-pink-400">const</span> <span className="text-blue-400">growthEngine</span> = <span className="text-yellow-400">await</span> aifabric.<span className="text-green-400">analyze</span>({`{`}</p>
                  <p className="pl-4"><span className="text-zinc-400">userBehavior</span>: <span className="text-emerald-400">realtime</span>,</p>
                  <p className="pl-4"><span className="text-zinc-400">conversionPath</span>: <span className="text-emerald-400">optimized</span>,</p>
                  <p className="pl-4"><span className="text-zinc-400">contentStrategy</span>: <span className="text-emerald-400">predictive</span>,</p>
                  <p>{`}`});</p>
                  <p className="mt-4 text-zinc-500">// Sonuç: +%240 dönüşüm artışı</p>
                  <p><span className="text-pink-400">return</span> <span className="text-emerald-400">growthEngine</span>.<span className="text-blue-400">results</span>;</p>
                </div>
                
                {/* Animated cursor */}
                <motion.div
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="inline-block w-2 h-5 bg-emerald-400 ml-1"
                />
              </div>
            </motion.div>

            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-xs font-mono text-emerald-400 bg-emerald-400/10 rounded-full border border-emerald-400/20">
                <Zap className="w-4 h-4" />
                AI-Powered Platform
              </span>
              <h2 className="heading-section text-white mb-6">
                aifabric: Büyümenizi
                <br />
                <span className="gradient-text">Otomatikleştiren Zeka</span>
              </h2>
              <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
                Yapay zeka destekli platformumuz, kullanıcı davranışlarını analiz eder, 
                dönüşüm yolculuğunu optimize eder ve büyüme stratejinizi 7/24 çalıştırır.
              </p>

              <div className="space-y-4 mb-10">
                {aifabricFeatures.map((feature, index) => (
                  <motion.div
                    key={feature}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="feature-check"
                  >
                    <div className="feature-check-icon">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="text-zinc-300">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <Link 
                to="/aifabric" 
                className="btn-glow inline-flex items-center gap-2"
                data-testid="aifabric-cta"
              >
                aifabric'i Keşfet
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="section-padding bg-[#121212]" data-testid="portfolio-section">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-16"
          >
            <div>
              <span className="text-xs font-mono text-pink-400 tracking-widest uppercase mb-4 block">
                Portföy
              </span>
              <h2 className="heading-section text-white">
                Büyüme Hikayeleri
              </h2>
            </div>
            <Link 
              to="/portfolyo" 
              className="mt-6 md:mt-0 text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
              data-testid="portfolio-view-all"
            >
              Tüm Portföyü Gör
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="portfolio-grid">
            {featuredProjects.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={`/proje/${item.slug}`} className="portfolio-card block" data-testid={`portfolio-item-${item.id}`}>
                  <img src={item.image} alt={item.title} loading="lazy" />
                  <div className="portfolio-overlay">
                    <span className="text-xs font-mono text-violet-400 mb-2">{item.categoryLabel}</span>
                    <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                    <p className="text-sm text-zinc-400 mb-3">{item.client}</p>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-2xl font-bold text-emerald-400">{item.metric}</span>
                      <span className="text-xs text-zinc-500">{item.metricLabel}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Taner Torun Metodolojisi - Premium/Iconic */}
      <section className="section-padding bg-[#0A0A0A]" data-testid="methodology-section">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="methodology-card-refined text-center max-w-4xl mx-auto">
              <div className="relative z-10">
                <img 
                  src="/images/brand/medyatik-logo-beyaz.png" 
                  alt="Medyatik" 
                  className="h-14 w-auto mx-auto mb-10 opacity-75"
                />
                <h2 className="heading-section text-white mb-6">
                  Zihin Mimarisi
                  <br />
                  <span className="gradient-text-refined tracking-[-0.01em]">Taner Torun İmzasıyla</span>
                </h2>
                <p className="text-zinc-300 text-lg leading-relaxed max-w-xl mx-auto mb-3">
                  Bu yaklaşım, markaların yalnızca görünmesini değil,
                  <br className="hidden sm:block" />
                  karar anlarında seçilmesini sağlar.
                </p>
                <p className="text-sm text-zinc-500 mb-10">
                  Davranış, algı ve karar mimarisi üzerine kurulu.
                </p>
                <Link 
                  to="/metodoloji" 
                  className="btn-gradient-refined inline-flex items-center gap-2"
                  data-testid="methodology-cta"
                >
                  Yaklaşımı Keşfet
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="section-padding bg-[#121212]" data-testid="stats-section">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-zinc-400 text-lg">
              <span className="text-white font-semibold">150+ marka</span> bize güvendi, 
              <span className="gradient-text font-semibold"> %240 ortalama büyüme</span> elde etti
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="stat-item"
                data-testid={`stat-${index}`}
              >
                <div className="stat-number">
                  <AnimatedCounter 
                    end={stat.value} 
                    prefix={stat.prefix || ''} 
                    suffix={stat.suffix || ''} 
                  />
                </div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Magnet / Free Analysis Section */}
      <section className="section-padding bg-[#121212]" data-testid="lead-magnet-section">
        <div className="container-wide">
          <LeadMagnet />
        </div>
      </section>

      {/* Final CTA Section - Updated */}
      <section className="section-padding relative overflow-hidden bg-[#0A0A0A]" data-testid="final-cta-section">
        {/* Subtle Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-zinc-900/20 to-transparent" />
        
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="heading-section text-white mb-6">
              Hazırsanız, Sisteme Bakalım.
            </h2>
            <p className="text-zinc-400 text-lg mb-10 leading-relaxed whitespace-pre-line">
              3 dakikalık bir analizle{'\n'}nerede büyüdüğünüzü,{'\n'}nerede kaybettiğinizi gösterelim.
            </p>
            
            <div className="flex flex-col items-center">
              <Link 
                to="/buyume-yolculugu" 
                className="btn-gradient-refined flex items-center gap-2"
                data-testid="final-cta-contact"
              >
                Büyüme Yolculuğunu Başlat
                <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-xs text-zinc-500 mt-4">Satış konuşması yok</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
